/** Shared “short mirror” copy: prefer multi-line AI summary; else compact snippets from every answer. */

import { stripLeadingOralFillers } from "./userTextFragments";

export const MIRROR_SNIPPET_MAX = 240;
/** Prompt snippet in mirror lines — keep short so question + answer fit on screen. */
export const MIRROR_QUESTION_HINT_MAX = 120;
/** One continuous “mirror” block (day-1 echo / language mirror) — total length cap */
export const UNIFIED_MIRROR_BODY_MAX_CHARS = 520;
/** Per-answer slice when merging journal replies into one block */
export const UNIFIED_MIRROR_PART_MAX_CHARS = 200;

export function normalizeMirrorWhitespace(text) {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t\u00a0]+/g, " ").trim())
    .filter(Boolean)
    .join("\n")
    .trim();
}

/**
 * Deterministic excerpt from combined journal text for “yesterday’s anchor” —
 * start-first, first sentence when possible (not a mid-text fragment).
 */
export function buildJourneyAnchorExcerpt(text, lang = "he", opts = {}) {
  const maxChars = opts.maxChars ?? 220;
  const minChars = opts.minChars ?? 16;
  let s = stripLeadingOralFillers(String(text || ""), lang);
  s = normalizeMirrorWhitespace(s).replace(/\n/g, " ");
  if (!s || s.length < minChars) return "";

  let cut = -1;
  const scanTo = Math.min(s.length, maxChars + 120);
  for (let i = minChars; i < scanTo; i += 1) {
    const ch = s[i];
    if (ch === "." || ch === "!" || ch === "?" || ch === "…") {
      if (i === s.length - 1 || /\s/u.test(s[i + 1] || " ")) {
        cut = i + 1;
        break;
      }
    }
  }

  if (cut >= minChars) {
    let out = s.slice(0, cut).trim();
    if (out.length <= maxChars) return out;
    let t = out.slice(0, maxChars - 1);
    const sp = t.lastIndexOf(" ");
    if (sp > maxChars * 0.55) t = t.slice(0, sp);
    return `${t.trimEnd()}…`;
  }

  if (s.length <= maxChars) return s;
  let tail = s.slice(0, maxChars - 1);
  const sp = tail.lastIndexOf(" ");
  if (sp > maxChars * 0.55) tail = tail.slice(0, sp);
  return `${tail.trimEnd()}…`;
}

export function truncateMirrorSnippet(text, max = MIRROR_SNIPPET_MAX) {
  const single = normalizeMirrorWhitespace(text).replace(/\n/g, " ");
  if (!single) return "";
  if (single.length <= max) return single;
  return `${single.slice(0, max - 1).trimEnd()}…`;
}

/** Strip common meta / “testing the app” noise from raw user text in mechanical fallback paths. */
export function sanitizeMirrorFallbackNoise(text, lang) {
  let s = normalizeMirrorWhitespace(String(text || "")).replace(/\n/g, " ");
  if (!s) return "";

  if (lang === "he") {
    s = s
      .replace(/\bאוקיי\b[,:]?\s*/gi, "")
      .replace(/\bokay\b[,:]?\s*/gi, "")
      .replace(/\bבסדר\b[,:]?\s*/gi, "")
      .replace(/אני רוצה לבדוק[^.!?…]{0,80}[.!?…]?/gi, "")
      .replace(/רק רציתי לבדוק[^.!?…]{0,80}[.!?…]?/gi, "");
  } else if (lang === "es") {
    s = s
      .replace(/\bok(ay)?\b[,:]?\s*/gi, "")
      .replace(/\bquiero comprobar\b[^.!?…]{0,80}[.!?…]?/gi, "")
      .replace(/\bsolo quería probar\b[^.!?…]{0,80}[.!?…]?/gi, "");
  } else {
    s = s
      .replace(/\bok(ay)?\b[,:]?\s*/gi, "")
      .replace(/\bi want to check\b[^.!?…]{0,100}[.!?…]?/gi, "")
      .replace(/\bjust wanted to (check|verify|test)\b[^.!?…]{0,100}[.!?…]?/gi, "");
  }

  return s.replace(/\s{2,}/g, " ").replace(/^\s*[.,;]\s*/g, "").trim();
}

export function pickIntakeAiSummary(intake) {
  const s = String(intake?.intakeSummary || "").trim();
  if (s.length < 40) return "";
  return s;
}

export function formatMirrorSummaryParagraphs(summary) {
  return summary
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, 8);
}

/** Editorial tweaks for stock Hebrew mirror phrasing (AI often echoes stiff patterns). */
export function polishHebrewMirrorPhrasing(text) {
  let s = String(text || "");
  if (!s) return "";

  s = s.replace(
    /אני מרגיש(ה)? לפעמים שאני עם אנשים אך לא באמת מחוברת אליהם/g,
    (_, fem) =>
      `אני מרגיש${fem || ""} שלפעמים אני עם אנשים, אבל לא באמת מחוברת אליהם`
  );
  s = s.replace(
    /אני מרגיש(ה)? לפעמים שאני עם אנשים אך לא באמת מחובר אליהם/g,
    (_, fem) =>
      `אני מרגיש${fem || ""} שלפעמים אני עם אנשים, אבל לא באמת מחובר אליהם`
  );
  s = s.replace(
    /אני מרגיש(ה)? לפעמים שאני עם אנשים אך לא באמת איתם/g,
    (_, fem) => `אני מרגיש${fem || ""} שלפעמים אני עם אנשים, אבל לא באמת איתם`
  );

  s = s.replace(
    /הכאב נובע מהמרחק שאני מרגיש(ה)?, שמקשה להתחבר באמת/g,
    (_, fem) =>
      `הכאב הוא לא רק המרחק שאני מרגיש${fem || ""}, אלא גם הקושי להתחבר באמת`
  );

  s = s.replace(
    /כי אני לא באמת מחוברת אליהם, כשאני נסגר/g,
    "כי גם כשאני ליד אנשים, אני לא באמת מרגישה מחוברת אליהם. כשאני נסגר"
  );
  s = s.replace(
    /כי אני לא באמת מחוברת אליהם כשאני נסגר/g,
    "כי גם כשאני ליד אנשים, אני לא באמת מרגישה מחוברת אליהם. כשאני נסגר"
  );
  s = s.replace(
    /כי אני לא באמת מחובר אליהם, כשאני נסגר/g,
    "כי גם כשאני ליד אנשים, אני לא באמת מרגיש מחובר אליהם. כשאני נסגר"
  );
  s = s.replace(
    /כי אני לא באמת מחובר אליהם כשאני נסגר/g,
    "כי גם כשאני ליד אנשים, אני לא באמת מרגיש מחובר אליהם. כשאני נסגר"
  );

  s = s.replace(/וזה גורם לי להרגיש מרוחק/g, "וזה משאיר אותי בתחושת ריחוק");

  s = s.replace(
    /הכאב הכי גדול הוא המרחק שאני לא מצליח(ה)? לגשר עליו ולהתחבר לאחרים\.?/g,
    (_, fem) =>
      `הכאב הכי גדול הוא התחושה שאני לא מצליח${fem || ""} לגשר על המרחק הזה ולהתחבר לאחרים`
  );

  return s;
}

/**
 * Collapse AI (or multi-line) mirror copy into one flowing block; trim length at word boundary.
 * @param {{ maxChars?: number, lang?: string }} [opts] — pass lang: "he" to apply Hebrew phrasing polish.
 */
export function formatMirrorSummaryAsFlowingBlock(summary, opts = {}) {
  const maxChars = opts.maxChars ?? UNIFIED_MIRROR_BODY_MAX_CHARS;
  const lang = opts.lang;
  let s = normalizeMirrorWhitespace(String(summary || "")).replace(/\n+/g, " ");
  if (!s) return "";
  if (lang === "he") {
    s = polishHebrewMirrorPhrasing(s);
  }
  if (s.length <= maxChars) return s;
  s = s.slice(0, maxChars - 1);
  const cut = Math.max(s.lastIndexOf(" "), s.lastIndexOf(" — "), s.lastIndexOf(" · "));
  if (cut > maxChars * 0.55) s = s.slice(0, cut);
  return `${s.trimEnd()}…`;
}

/**
 * Day-1 emotional echo: one short, continuous mirror from the user’s writing (no repeated per-question frame).
 */
/**
 * @param {{ maxParts?: number, maxTotalChars?: number }} [opts] — omit for default “short mirror”; journey final summary may pass larger caps.
 */
export function buildUnifiedEmotionalEchoBody(journalEntries, lang, opts = {}) {
  const maxParts = Math.min(12, Math.max(1, Number(opts.maxParts) || 3));
  const maxTotalChars = Math.max(
    UNIFIED_MIRROR_BODY_MAX_CHARS,
    Number(opts.maxTotalChars) || UNIFIED_MIRROR_BODY_MAX_CHARS
  );
  const list = Array.isArray(journalEntries) ? journalEntries : [];
  const parts = [];
  for (let i = 0; i < list.length && parts.length < maxParts; i += 1) {
    const cleaned = sanitizeMirrorFallbackNoise(list[i]?.text || "", lang);
    const answer = truncateMirrorSnippet(cleaned, UNIFIED_MIRROR_PART_MAX_CHARS);
    if (answer) parts.push(answer);
  }
  if (!parts.length) return "";

  const stripTrail = (p) => p.replace(/[.!?…,:;\s]+$/u, "").trim();

  let out;
  if (lang === "he") {
    if (parts.length === 1) out = parts[0];
    else out = parts.map(stripTrail).filter(Boolean).join(" — ");
  } else {
    const cleaned = parts.map(stripTrail).filter(Boolean);
    out = cleaned.join(". ");
    if (cleaned.length > 1 && out && !/[.!?…]$/.test(out)) out += ".";
  }

  if (out.length > maxTotalChars) {
    let t = out.slice(0, maxTotalChars - 1);
    const cut = Math.max(t.lastIndexOf(" "), t.lastIndexOf("."), lang === "he" ? t.lastIndexOf(" — ") : -1);
    if (cut > maxTotalChars * 0.5) t = t.slice(0, cut);
    out = `${t.trimEnd()}…`;
  }
  if (lang === "he") {
    out = polishHebrewMirrorPhrasing(out);
  }
  return out;
}

/**
 * Single paragraph fallback when there is no AI intake summary (language-mirror page).
 */
export function buildIntakeMirrorSingleParagraph(intake, labels, lang = "he") {
  const rows = buildIntakeMirrorSnippets(intake, labels, lang);
  if (!rows.length) return "";
  const parts = rows.map((row) => {
    const label = String(row.label || "").trim().replace(/:\s*$/, "");
    const text = String(row.text || "").trim();
    if (!text) return "";
    return label ? `${label}: ${text}` : text;
  }).filter(Boolean);
  if (!parts.length) return "";
  return parts.join(" · ");
}

export function buildIntakeMirrorSnippets(intake, labels, lang = "he") {
  const rows = [
    { key: "pain", label: labels.pain, raw: intake?.painNow },
    { key: "goal", label: labels.goal, raw: intake?.desiredEndState },
    { key: "word", label: labels.word, raw: intake?.oneWord },
  ];
  return rows
    .map((row) => ({
      key: row.key,
      label: row.label,
      text: truncateMirrorSnippet(sanitizeMirrorFallbackNoise(row.raw, lang)),
    }))
    .filter((row) => row.text);
}

/**
 * @param {{ text?: string, questionText?: string }[]} entries
 * @param {{ formatLine: (args: { questionHint: string, answer: string, index: number }) => string }} options
 */
export function buildJournalMirrorLines(entries, options) {
  const formatLine = options?.formatLine;
  if (typeof formatLine !== "function") return [];
  const out = [];
  const list = Array.isArray(entries) ? entries : [];
  let index = 0;
  for (let i = 0; i < list.length; i += 1) {
    const answer = truncateMirrorSnippet(list[i]?.text || "");
    if (!answer) continue;
    index += 1;
    const qRaw = String(list[i]?.questionText || "")
      .trim()
      .replace(/[«»]/g, '"');
    const questionHint = qRaw ? truncateMirrorSnippet(qRaw, MIRROR_QUESTION_HINT_MAX) : "";
    out.push(formatLine({ questionHint, answer, index }));
    if (out.length >= 6) break;
  }
  return out;
}

export function intakeHasMirrorSource(intake) {
  if (pickIntakeAiSummary(intake)) return true;
  const bits = [intake?.painNow, intake?.desiredEndState, intake?.oneWord].map((v) =>
    String(v || "").trim()
  );
  return bits.some(Boolean);
}
